import { z, setErrorMap } from "zod";

// -----------------------------------------------------------------------------
// Override global para campos requeridos y tipos faltantes → "Required"
// -----------------------------------------------------------------------------
setErrorMap((issue, ctx) => {
    if (
        (issue.code === 'invalid_type' && issue.received === 'undefined') ||
        issue.code === 'too_small'
    ) {
        return { message: 'Requerido' };
    }
    // Preservar mensajes personalizados para otros tipos de error
    if (issue.message) {
        return { message: issue.message };
    }
    return { message: ctx.defaultError };
});

// Expresión regular para permitir solo letras (mayúsculas/minúsculas), acentos y espacios
const onlyLettersRegex = /^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+$/;
// Expresión regular para permitir solo dígitos
const digitsRegex = /^\d+$/;
// Expresión regular para permitir letras, dígitos y espacios (incluyendo acentos)
const alphaNumWithSpace = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s]+$/;  // letras, dígitos y espacios
// Expresión regular para URLs
const urlRegex = /^https?:\/\/.+/;
// Expresión regular para emails más estricta
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// -----------------------------------------------------------------------------
// Schema de Usuario
// -----------------------------------------------------------------------------
export const usuarioSchema = z.object({
    documento_identificacion: z.string().regex(digitsRegex, { message: 'Solo dígitos' }).min(1, { message: 'Requerido' }),
    nombre: z.string().regex(onlyLettersRegex, { message: 'Solo letras' }).min(1, { message: 'Requerido' }),
    apellidos: z.string().regex(onlyLettersRegex, { message: 'Solo letras' }).optional(),
    celular: z.string().regex(digitsRegex, { message: 'Solo dígitos' }).min(1, { message: 'Requerido' }),
    correo_electronico: z.string().regex(emailRegex, { message: 'Email inválido' }).min(1, { message: 'Requerido' }),
    contrasena: z.string().min(6, { message: 'Mínimo 6 caracteres' }),
});

// -----------------------------------------------------------------------------
// Schema de Empresa
// -----------------------------------------------------------------------------
export const empresaSchema = z.object({
    nit: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ #,./-]+$/, { message: 'Solo dígitos' }).min(1, { message: 'Requerido' }),
    nombre_empresa: z.string().regex(alphaNumWithSpace, { message: 'Solo letras y números' }).min(1, { message: 'Requerido' }),
    celular: z.string().regex(digitsRegex, { message: 'Solo dígitos' }).min(1, { message: 'Requerido' }),
    direccion: z.string().regex(/^[A-Za-z0-9 #,.-]+$/, { message: 'Formato inválido' }).min(1, { message: 'Requerido' }),
    host_empresa: z.string().min(1, { message: 'Requerido' }),
    tenant: z.string().optional() // Campo opcional ya que se genera automáticamente
});

// -----------------------------------------------------------------------------
// Schema de Codigo - Empresa
// -----------------------------------------------------------------------------
export const codigoEmpresaSchema = z.object({
    codigo: z.string().regex(/^[A-Za-z0-9-_]+$/, { message: 'Formato de código inválido' }).min(1, { message: 'Requerido' }),
});

// -----------------------------------------------------------------------------
// Schema de Host
// -----------------------------------------------------------------------------
export const hostSchema = z.object({
    url: z.string().regex(urlRegex, { message: 'URL inválida' }).min(1, { message: 'Requerido' }),
    estado: z.boolean({ message: 'Requerido' }),
    descripcion: z.string().min(1, { message: 'Requerido' })
});

// -----------------------------------------------------------------------------
// Schema de Encuesta
// -----------------------------------------------------------------------------
export const encuestaSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    objetivo: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    id_empresa: z.union([
        z.string().min(1, { message: 'Requerido' }),
        z.number().int().positive({ message: 'Debe ser un número positivo' })
    ]),
    numero_preguntas: z.union([
        z.string().regex(digitsRegex, { message: 'Solo dígitos' }).min(1, { message: 'Requerido' }),
        z.number().int().nonnegative({ message: 'Debe ser un número válido' })
    ]),
    tiempo_desarrollo: z.string().regex(/^\d{2}:\d{2}$/, { message: 'Formato HH:MM requerido' }).min(1, { message: 'Requerido' })
});

// -----------------------------------------------------------------------------
// Schema de Pregunta
// -----------------------------------------------------------------------------
export const preguntaSchema = z.object({
    enunciado: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    tema: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    numero_opciones_respuesta: z.union([
        z.string().min(1, { message: 'Requerido' }).regex(digitsRegex, { message: 'Solo dígitos' }),
        z.number().int().nonnegative({ message: 'Debe ser un número válido' }).transform(String)
    ]),
    porcentaje: z.union([
        z.string().min(1, { message: 'Requerido' }).regex(/^\d+(\.\d{1,4})?$/, { message: 'Formato numérico válido' }),
        z.number().nonnegative({ message: 'Debe ser un número válido' }).transform(String)
    ]),
    idEncuesta: z.number().int().positive({ message: 'Debe ser un número positivo' }),
});

// Schema para el formulario (frontend)
export const respuestaFormSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    verdadera: z.union([
        z.string().transform(val => val === '' ? null : (val === '1' ? 1 : 0)),
        z.number().int().min(0).max(1)
    ]).optional(),
    id_tipo_dato: z.union([
        z.string().min(1, { message: 'Requerido' }).transform(val => parseInt(val, 10)),
        z.number().int().min(1, { message: 'Requerido' })
    ]),
    aplica_observacion: z.union([
        z.string().transform(val => val === '' ? null : (val === '1' ? 1 : 0)),
        z.number().int().min(0).max(1)
    ]).optional(),
    pregunta: z.union([
        z.number().int().min(1, { message: 'Requerido' }),
        z.string().min(1, { message: 'Requerido' }).transform(val => parseInt(val, 10))
    ]),
});

// Schema para el servidor (backend)
export const respuestaSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    verdadera: z.union([
        z.number().int().min(0).max(1),
        z.string().transform(val => val === '1' ? 1 : 0)
    ]).optional(),
    datoRespuesta: z.union([
        z.number().int().min(1, { message: 'Requerido' }),
        z.string().min(1, { message: 'Requerido' }).transform(val => parseInt(val, 10))
    ]),
    aplica_observacion: z.union([
        z.number().int().min(0).max(1),
        z.string().transform(val => val === '1' ? 1 : 0)
    ]).optional(),
    pregunta: z.union([
        z.number().int().min(1, { message: 'Requerido' }),
        z.string().min(1, { message: 'Requerido' }).transform(val => parseInt(val, 10))
    ]),
});

// -----------------------------------------------------------------------------
// Schema de Tema
// -----------------------------------------------------------------------------
export const temaSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    descripcion: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
});

// -----------------------------------------------------------------------------
// Schema de Tipo de Pregunta
// -----------------------------------------------------------------------------
export const tipoPreguntaSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    componente: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    descripcion: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
});

// -----------------------------------------------------------------------------
// Schema de Tipo de Dato
// -----------------------------------------------------------------------------
export const tipoDatoSchema = z.object({
    nombre: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
    descripcion: z.string().regex(/^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñ\s#,.;:¿?¡!()[\]{}'"`/_\-@%&*+=\\|~<>$]+$/, { message: 'Formato de texto inválido' }).min(1, { message: 'Requerido' }),
});